import { appIsLive, getUserAdapter, isMyAccountOrLoginPage } from "../gtm/GtmUtils";
import { makeJsonRequest } from "./JsonRequests";
import { HttpMethods } from "./HttpMethods";
import { LogLevels } from "./LogLevels";
import { CookiesNames } from "../gtm/Cookies";
import { getCookie } from "typescript-cookie";
import { container } from "tsyringe";
import { ServiceTokens } from "../shared/ServiceTokens";
import { PageAttributesExtractor } from "../shared/PageAttributesExtractor";
import { PageDebugInfo } from "../shared/PageDebugInfo";
import { SiteSettings } from "../shared/SiteSettings";

/**
 * Indicate the URL used to send logs.
 * Initialized at compile time via WebPack.
 */
declare const LogsUrl: string;

/**
 * Logger that will log messages in the console ony when the app is not live or the GTM_DEBUG switch is enabled.
 */
export class Logger {
  private static instance: Logger;
  private readonly enabled: boolean = false;
  private allMessages = [] as string[];
  private truncatedText = "[Log truncated...]";
  private truncatedLogsCount = 0;
  private maxPropertyLength = 10000;
  private maxPropertiesCount = 30;
  private hasSentPreviousLog = false;

  /**
   * Unique identifier generated once per browser to correlate logs.
   * @private
   */
  private browserId = "";

  /**
   * Unique identifier generated once per library load to correlate logs.
   * Should generate on every page reload and be different for each tab navigation
   * @private
   */
  private readonly gtmLibSessionId: string;
  private readonly siteName: string;

  private constructor() {
    const siteSettings = container.resolve<SiteSettings>(ServiceTokens.SiteSettings);
    this.enabled =
      (!appIsLive(siteSettings.ProductionHostNames) && !isMyAccountOrLoginPage()) ||
      localStorage.getItem("GTM_DEBUG") === "true";
    this.gtmLibSessionId = crypto.randomUUID();
    this.siteName = (container.resolve(ServiceTokens.SiteSettings) as SiteSettings).SiteName;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public log(...args: any): void {
    if (this.enabled) {
      console.log("[DSL]", ...args);
    }
    let logMessage: string;

    if (typeof args === "string") {
      logMessage = args;
    } else if (Array.isArray(args)) {
      const lineParts: string[] = args.map((element) => {
        return typeof element === "string" ? element : JSON.stringify(element);
      });
      logMessage = lineParts.join(" ");
    } else {
      logMessage = JSON.stringify(args);
    }
    this.allMessages.push(logMessage);
    this.validateAllMessages();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public verbose(...args: any): void {
    if (this.enabled) {
      console.log("[DSL]", ...args);
    }
  }

  public debugLog(message: string): void {
    if (this.enabled) {
      console.log("[DSL]", message);
    }
    const body = {
      level: LogLevels.Debug,
      message: message,
      uri: window.location.href,
      properties: this.getAdditionalLogProperties(),
    };
    this.sendLogMessage(body, true);
  }

  public error(message: string, properties: object | undefined): void {
    console.error("[DSL]", message, properties);
    const body = {
      level: LogLevels.Error,
      message: message,
      uri: window.location.href,
      properties: this.getAdditionalLogProperties(properties),
    };

    this.sendLogMessage(body);
  }

  public warn(message: string): void {
    console.warn("[DSL]", message);
    const body = {
      level: LogLevels.Warning,
      message: message,
      uri: window.location.href,
      properties: this.getAdditionalLogProperties(),
    };
    this.sendLogMessage(body);
  }

  /**
   * Log the error to the console and send it to Kibana
   * @param message The message to send
   * @param properties Additional log message properties
   */
  public static error(message: string, properties: object | undefined = undefined): void {
    Logger.getInstance().error(message, properties);
  }

  /**
   * Log an error and sent it to Kibana, including details about the failed request.
   * @param message The message to send
   * @param response The response to a failed request
   * @param properties Additional properties to log
   */
  public static async requestError(
    message: string,
    response: Response,
    properties: object | undefined = undefined,
  ): Promise<void> {
    const body = await response.text();
    const responseProperties = {
      "fetch.responseBody": body,
      "fetch.responseType": response.type,
      "fetch.statusCode": response.status,
      "fetch.url": response.url,
    };
    const allProperties = {
      ...responseProperties,
      ...properties,
    };
    Logger.error(message, allProperties);
  }

  /**
   * Log the message to the console. No data is sent to Kibana
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static log(...args: any): void {
    Logger.getInstance().log(...args);
  }

  /**
   * Log the message to the console and send it to Kibana as debug
   * @param message The message to send
   */
  public static debugLog(message: string): void {
    Logger.getInstance().debugLog(message);
  }

  /**
   * Log the warning the console and send it to Kibana
   * @param message The message to send
   */
  public static warn(message: string): void {
    Logger.getInstance().warn(message);
  }

  /**
   * Log the message to the console. No data is sent to Kibana and the message will not be saved to be sent to Marea when an error occurs
   * @param args Arguments to be logged
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static verbose(...args: any): void {
    Logger.getInstance().verbose(...args);
  }

  /**
   * Checks if the app is running in live mode by comparing the production host name.
   * Logs a message indicating whether the app is in live or development mode.
   */
  public static LogEnvironmentMode(): void {
    const siteSettings = container.resolve<SiteSettings>(ServiceTokens.SiteSettings);
    if (appIsLive(siteSettings.ProductionHostNames)) {
      Logger.verbose("Running in **live** mode!");
    } else {
      Logger.verbose("Running in dev mode!");
    }
  }

  /** Follows the singleton pattern */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Send log message to Kibana
   * @param body
   * @param includePreviousLogs If the previous messages should be kept or not. Used in debug to help navigation in Kibana
   */
  private sendLogMessage(body: logMessage, includePreviousLogs = false): void {
    // Truncate the properties to keep the payload size under the limit
    body.properties.forEach((kvp) => {
      if (typeof kvp.value === "string" && kvp.value.length > this.maxPropertyLength) {
        kvp.value = kvp.value.substring(0, this.maxPropertyLength - this.truncatedText.length) + this.truncatedText;
      }
    });

    // Limit the number of properties count
    body.properties = body.properties.filter((kvp) => kvp.key && kvp.value).slice(0, this.maxPropertiesCount);
    const logMessagesSent = this.allMessages.length;
    makeJsonRequest(LogsUrl, HttpMethods.POST, "omit", body)
      .then((response) => {
        // Remove messages successfully sent to Marea to save on payload size.
        if (response.ok && !includePreviousLogs) {
          this.allMessages.splice(0, logMessagesSent);
          this.hasSentPreviousLog = true;
        }
      })
      .catch((reason) => {
        console.error("Failed to send log. Reason:", reason);
      });
  }

  /**
   * Validate the length of the messages and properties to keep the payload size under the limit
   * @private
   */
  private validateAllMessages(): void {
    // Remove the oldest messages to keep the property length under the limit
    let totalLength = this.allMessages.reduce((sum, message) => sum + message.length, 0);
    while (totalLength > this.maxPropertyLength) {
      const removedMessage = this.allMessages.shift();
      this.truncatedLogsCount++;
      if (removedMessage) {
        totalLength -= removedMessage.length;
      }
    }
  }

  /**
   * Get the additional properties to log
   * @param properties Additional properties to log
   * @private
   */
  private getAdditionalLogProperties(properties: object | undefined = undefined): KeyValuePair[] {
    const logProperties = [] as KeyValuePair[];
    if (properties) {
      this.addObjectProperties(logProperties, properties);
    }

    logProperties.push({ key: "jsBuildNumber", value: BuildNumber });
    logProperties.push({ key: "browserId", value: this.getBrowserId() });
    logProperties.push({ key: "gtmLibSessionId", value: this.gtmLibSessionId });
    logProperties.push({ key: "gtmContext", value: this.getContext() });
    logProperties.push({ key: "cookiesEnabled", value: navigator.cookieEnabled });
    logProperties.push({ key: "cookieSize", value: document.cookie?.length ?? 0 });
    logProperties.push({ key: "siteName", value: this.siteName });
    if (this.hasSentPreviousLog) {
      this.allMessages.unshift("**Logs where sent in a previous message**");
    }
    if (this.truncatedLogsCount > 0) {
      this.allMessages.unshift(`**${this.truncatedLogsCount} logs were truncated**`);
      this.truncatedLogsCount = 0;
    }
    logProperties.push({ key: "allLogs", value: this.allMessages.join("\n") });
    const user = getUserAdapter();
    if (user?.rcId) {
      logProperties.push({ key: "RcId", value: user.rcId });
    }
    if (user?.rc_visitor_id) {
      logProperties.push({ key: "RcVisitorId", value: user.rc_visitor_id });
    }
    this.addCookies(logProperties);

    this.addObjectProperties(logProperties, this.getPageProperties());
    return logProperties;
  }

  /**
   * Get the unique identifier for the browser
   * @private
   */
  private getBrowserId(): string {
    const storageKey = "DS_LogId";
    if (this.browserId) {
      return this.browserId;
    }
    const storageValue = localStorage.getItem(storageKey);
    if (storageValue) {
      this.browserId = storageValue;
      return this.browserId;
    }
    this.browserId = crypto.randomUUID();
    localStorage.setItem(storageKey, this.browserId);
    return this.browserId;
  }

  /**
   * Get the GTM context to log
   * @private
   */
  private getContext(): string {
    const contextData = [] as string[];

    // Limit the number of messages to the last 50
    const maxMessages = 50;
    const queueLength = (dataLayerQueue as []).length;
    const startIndex = Math.max(queueLength - maxMessages, 0);
    const lastMessages = (dataLayerQueue as object[]).slice(startIndex, queueLength);

    const user = getUserAdapter();
    if (!user) {
      contextData.push("User not available in DataLayer");
    }
    if (typeof blueConicClient !== "undefined" && typeof blueConicClient.profile !== "undefined") {
      contextData.push(`BlueConic Profile Id: ${blueConicClient.profile?.getProfile()?.getId()}`);
    }

    contextData.push("\nDataLayerQueue changes history: ");
    if (startIndex !== 0) {
      contextData.push(`\n... ${startIndex} previous messages in the DataLayerQueue truncated`);
    }

    const availableLength = this.maxPropertyLength - contextData.reduce((sum, line) => sum + line.length, 0);

    const dataLayerLines = [] as string[];
    lastMessages.forEach((element: object, index) => {
      let stringValue = undefined;
      if ("event" in element && "user" in element) {
        try {
          stringValue = JSON.stringify({ event: element.event, user: element.user });
        } catch (error) {
          stringValue = `\nCouldn't stringify the user. Event: ${element.event} Error: ${error}`;
        }
      } else if ("user" in element) {
        try {
          dataLayerLines.push(JSON.stringify({ event: "without-event", user: element.user }));
        } catch (error) {
          stringValue = `\nCouldn't stringify the user. Event: without-event. Error : ${error}`;
        }
      } else if ("event" in element) {
        try {
          stringValue = JSON.stringify({ event: element.event });
        } catch (error) {
          stringValue = `\nCouldn't stringify the event name. Event without user changes. Error : ${error}`;
        }
      }
      if (!stringValue) {
        return;
      }
      // If the message is the same as the last one, don't add it to the list
      if (index > 0 && dataLayerLines.length > 0 && dataLayerLines[dataLayerLines.length] === stringValue) {
        return;
      }
      dataLayerLines.push(stringValue);
    });

    // Remove the oldest DataLayerQueue event to keep the message count under the limit
    let eventsLength = dataLayerLines.reduce((sum, line) => sum + line.length, 0);
    while (eventsLength > availableLength) {
      const removedMessage = dataLayerLines.shift();
      this.truncatedLogsCount++;
      if (removedMessage) {
        eventsLength -= removedMessage.length;
      }
    }

    return [...contextData, ...dataLayerLines].join("\n");
  }

  private addObjectProperties(logProperties: KeyValuePair[], obj: object): void {
    for (const [key, value] of Object.entries(obj)) {
      logProperties.push({ key: key, value: value });
    }
  }

  private getPageProperties(): PageDebugInfo {
    const pageAttributesExtractor = container.resolve<PageAttributesExtractor>(ServiceTokens.PageAttributesExtractor);
    return pageAttributesExtractor.extractDebugInfo();
  }

  private addCookies(logProperties: KeyValuePair[]): void {
    const cookieNames = Object.values(CookiesNames);
    cookieNames.forEach((cookieName) => {
      const cookieValue = getCookie(cookieName);
      if (cookieValue) {
        logProperties.push({ key: `cookie.${cookieName}`, value: cookieValue });
      }
    });
  }
}

interface logMessage {
  level: LogLevels;
  message: string;
  uri: string;
  properties: KeyValuePair[];
}

interface KeyValuePair {
  key: string;
  value: unknown;
}
